import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError, handleApiError } from "../errors";
import schemaValidators from "./schema-validators";
import { TypeFromSchemaValidator } from "../schema-validator";
import lmsInfoSchemaValidators from "../lms-info/schema-validators";
import { AssessmentInfo } from "../types";

export default class AssessmentClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getAssessments = async ({ course }: { course: { id: number } }) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${course.id}/assessments`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.assessmentInfoArray.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		return [];
	};

	useAssessmentsWithTypeCount = async ({
		instituteId,
		courseId,
		time,
	}: {
		instituteId: string;
		courseId: number;
		time: string;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/institute/${instituteId}/courseId/${courseId}/time/${time}`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.assessmentMonthlyCountList.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
	};

	getCourseAssessments = async (lmsCourseId: string) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/assessments`,
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.assessmentInfoList.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		return [];
	};

	getAssessmentByCourseQuizId = async ({ lmsQuizId, courseId }: { lmsQuizId: string; courseId: number }) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/course/${courseId}/lms-assessment/${lmsQuizId}`, `${this.config.verityOrigin}`),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.assessmentInfo.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
	};

	getAssessmentByAssessmentInfo = async (
		assessmentInfo: Pick<
			TypeFromSchemaValidator<(typeof lmsInfoSchemaValidators)["assessmentInfo"]>,
			"courseId" | "quizId"
		>,
	) => {
		try {
			const resp = await super.fetch(
				new URL(
					`/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}`,
					`${this.config.verityOrigin}`,
				),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.assessmentInfo.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
	};

	getAssessmentByAssessmentId = async (assessmentId: number) => {
		try {
			const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
				credentials: "include",
			});
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.assessmentInfo.validate(data)) {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
	};

	isAssessmentVerityEnabled = async (
		assessmentInfo: Pick<
			TypeFromSchemaValidator<(typeof lmsInfoSchemaValidators)["assessmentInfo"]>,
			"courseId" | "quizId"
		> & { hostname?: string; instituteId?: number },
	) => {
		try {
			const searchParams = new URLSearchParams();
			if (assessmentInfo.instituteId) {
				searchParams.append("instituteId", assessmentInfo.instituteId.toString());
			}
			if (assessmentInfo.hostname) {
				searchParams.append("hostname", assessmentInfo.hostname);
			}
			const resp = await super.fetch(
				new URL(
					`/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/proctored?${searchParams.toString()}`,
					`${this.config.verityOrigin}`,
				),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (typeof data === "boolean") {
					return data;
				}
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	assessmentCopyCheck = async (
		assessmentInfo: Pick<
			TypeFromSchemaValidator<(typeof lmsInfoSchemaValidators)["assessmentInfo"]>,
			"courseId" | "quizId"
		> & { hostname?: string; instituteId?: number } & { isCanvasNewQuiz?: boolean },
	) => {
		try {
			const type = assessmentInfo.isCanvasNewQuiz ? "canvas-new-quiz" : undefined;
			const searchParams = new URLSearchParams();
			if (assessmentInfo.instituteId) {
				searchParams.append("instituteId", assessmentInfo.instituteId.toString());
			}
			if (assessmentInfo?.hostname) {
				searchParams.append("hostname", assessmentInfo.hostname);
			}
			if (type) {
				searchParams.append("type", type);
			}
			const resp = await super.fetch(
				new URL(
					`/api/verity/lms-course/${assessmentInfo.courseId}/lms-assessment/${assessmentInfo.quizId}/copy-check?${searchParams.toString()}`,
					`${this.config.verityOrigin}`,
				),
				{ method: "PUT", credentials: "include" },
			);
			if (resp.ok) {
				return;
			}
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	getStandaloneStudents = async (assessmentId: number) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/standalone-api/assessment/${assessmentId}/students`, `${this.config.verityOrigin}`),
				{ credentials: "include" },
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.standaloneStudentList.validate(data)) {
					return data;
				}
				throw new Error(`Invalid shape of response`);
			}
			throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	linkStandaloneStudentUser = ({
		assessmentId,
		standaloneStudentId,
		accountUuid,
		userUuid,
	}: {
		assessmentId: number;
		standaloneStudentId: number;
		accountUuid: string;
		userUuid: string;
	}) => {
		return handleApiError(
			super.fetch(
				new URL(
					`/api/verity/standalone-api/assessment/${assessmentId}/student/${standaloneStudentId}/map`,
					`${this.config.verityOrigin}`,
				),
				{
					method: "PUT",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ accountUuid, userUuid }),
				},
			),
		);
	};

	deleteStandaloneAssessmentByUUID = async (assessmentUUID: string, courseId: string) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/standalone-api/assessment/${assessmentUUID}/${courseId}`, `${this.config.verityOrigin}`),
				{ method: "DELETE", credentials: "include" },
			);
			if (resp.ok) {
				return { success: true };
			}
			throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	updateLmsAssessmentNameSuffix = async ({
		assessmentId,
		oldSuffix,
		newSuffix,
	}: {
		assessmentId: number;
		oldSuffix: string | null;
		newSuffix: string | null;
	}) => {
		try {
			const resp = await super.fetch(
				new URL(`/api/verity/assessment/${assessmentId}/suffix`, `${this.config.verityOrigin}`),
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						oldSuffix,
						newSuffix,
					}),
				},
			);
			if (resp.ok) {
				const data: unknown = await resp.json();
				if (this.schemaValidators.suffixUpdateResult.validate(data)) {
					return data;
				}
				throw new Error(`Invalid shape of response`);
			}
			throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	/**
	 * Validate the assessments configuration against the LMS and
	 * return the results
	 */
	validate = async ({ courseId, quizId }: Pick<AssessmentInfo, "courseId" | "quizId">) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/lms-course/${courseId}/lms-assessment/${quizId}/validate`, `${this.config.verityOrigin}`),
				{
					method: "GET",
					headers: { "Content-Type": "application/json" },
				},
			);

			if (response.ok) {
				const data: unknown = await response.json();
				if (this.schemaValidators.validate.validate(data)) {
					return data;
				}
				throw new Error(`Invalid shape of response`);
			}

			throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};

	/**
	 * Synchronize the given assessment's password between the
	 * LMS and our database if needed
	 */
	synchronize = async ({ courseId, quizId }: Pick<AssessmentInfo, "courseId" | "quizId">) => {
		try {
			const response = await super.fetch(
				new URL(
					`/api/verity/lms-course/${courseId}/lms-assessment/${quizId}/synchronize`,
					`${this.config.verityOrigin}`,
				),
				{
					method: "GET",
					headers: { "Content-Type": "application/json" },
				},
			);

			if (response.ok) {
				const data: unknown = await response.json();
				if (this.schemaValidators.synchronize.validate(data)) {
					return data;
				}
				throw new Error(`Invalid shape of response`);
			}

			throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
		} catch (e) {
			if (e instanceof Error) {
				throw new ApiClientError(e);
			}
		}
		throw new ApiClientError();
	};
}
